import { useState, useCallback, useMemo } from "react"
import { useConnectorControllerGetAllConnectorsQuery as useGetAllConnectorsQuery } from "~/ui-rtk/api/connectorApi"
import { useCompanyConnectorControllerGetCompanyConnectorsQuery as useGetCompanyConnectorsQuery } from "~/ui-rtk/api/companyConnectorApi"

import { useAppSelector } from "~/ui-rtk/app/hooks"
import { selectUserSessionRole } from "~/ui-rtk/app/selectors/user.selector"
import { useSetupDetails } from "./hooks/setup-details"
import useAccess from "~/ui-rtk/hooks/access"

export const useConnect = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const sessionRole = useAppSelector(selectUserSessionRole)
  const { isSuperAdmin, isBillingEnabled, isSosFree } = useAccess()

  const {
    data: connectors,
    isLoading: isConnectorsLoading,
    isError: isConnectorsError,
  } = useGetAllConnectorsQuery()

  const {
    data: companyConnectors,
    isLoading: isCompanyConnectorsLoading,
    isError: isCompanyConnectorsError,
  } = useGetCompanyConnectorsQuery()
  const isSetupDetailsLoading =
    isConnectorsLoading || isCompanyConnectorsLoading
  const isSetupDetailsError = isConnectorsError || isCompanyConnectorsError

  const setupDetails = useSetupDetails(connectors, companyConnectors)

  // Free SoS companies used to see the full product nav as an upsell surface
  // (APP-453/APP-485); since APP-493 they get a dedicated minimal nav
  // (sosFreeSidebarItems) instead, so the product nav is only for super
  // admins and billing-enabled companies. Deep links to paid routes still
  // hit SubscriptionGuard's paywall.
  const showSidebarProducts = useMemo(
    () => isSuperAdmin || isBillingEnabled,
    [isSuperAdmin, isBillingEnabled],
  )

  const handleBeforeNavigate = useCallback(() => {
    // continue navigating if already expanded
    if (isExpanded) return true

    setIsExpanded(true)

    // stop navigating if not expanded
    return false
  }, [isExpanded, setIsExpanded])

  const handleNavigate = useCallback(() => {
    setIsExpanded(false)
  }, [setIsExpanded])

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(previous => !previous)
  }, [setIsExpanded])

  const collapse = () => {
    setIsExpanded(false)
  }

  const expand = () => {
    setIsExpanded(true)
  }

  return {
    showSidebarProducts,
    isSosFree,
    isExpanded,
    sessionRole,
    handleBeforeNavigate,
    handleNavigate,
    handleToggleExpand,
    expand,
    collapse,
    setupDetails,
    isSetupDetailsLoading,
    isSetupDetailsError,
  }
}
